import {
  GALLERY_COLUMN_SLUGS_CORE,
  GALLERY_COLUMN_SLUGS_META,
  GALLERY_DISPLAY_DEFAULT,
  GALLERY_SORTBY_SLUGS_CARD_REQUIRED,
  type GalleryDisplayType,
  type GallerySortbySlugType,
  isGalleryColumnSlugsCoreType,
  isGalleryColumnSlugType,
  isGallerySizeSlugType,
  isGalleryViewSizeable,
} from '@/defs/gallery'
import { type PriceSourceType } from '@/defs/price'
import { type GalleryStateType } from '@/store/gallery'
import { uniqueKeys } from '@/utils/commonUtil'

import { getGalleryStats } from './getGalleryStats'

type Props = {
  config: Partial<GalleryDisplayType>
  prefs: GalleryStateType
  priceSources: PriceSourceType[]
}

export const getGalleryDisplay = ({ config, prefs, priceSources }: Props): GalleryDisplayType => {
  const { cardFilters, columnsPref, groupbyPref, sizePref, sortbyDirectionPref, sortbyPref, viewPref } = prefs

  const viewDefault = config.view || GALLERY_DISPLAY_DEFAULT.view
  const view = viewPref || viewDefault

  const sizeDefault = config.size || GALLERY_DISPLAY_DEFAULT.size
  const sizeValid = isGalleryViewSizeable(view) && isGallerySizeSlugType(sizePref) ? sizePref : undefined
  const size = sizeValid || sizeDefault

  const groupbySlugs = uniqueKeys([...(config.groupbySlugs || []), ...GALLERY_DISPLAY_DEFAULT.groupbySlugs])
  const groupbyDefault = config.groupby || GALLERY_DISPLAY_DEFAULT.groupby
  const groupbyValid = groupbyPref && groupbySlugs.includes(groupbyPref) ? groupbyPref : undefined
  const groupby = groupbyValid || groupbyDefault

  const sortbyOptions = uniqueKeys<GallerySortbySlugType>([
    ...(config.sortbyOptions || []),
    ...GALLERY_DISPLAY_DEFAULT.sortbyOptions,
  ])
  const sortbyDefault = config.sortby || GALLERY_DISPLAY_DEFAULT.sortby
  const sortbyValid = sortbyPref && sortbyOptions.includes(sortbyPref) ? sortbyPref : undefined
  const sortby = sortbyValid || sortbyDefault

  const sortbyDirectionDefault = config.sortbyDirection || GALLERY_DISPLAY_DEFAULT.sortbyDirection
  const sortbyDirection = sortbyDirectionPref || sortbyDirectionDefault

  const sortbyDefaultIcon = config.sortbyDefaultIcon || GALLERY_DISPLAY_DEFAULT.sortbyDefaultIcon
  const sortbyDefaultLabel = config.sortbyDefaultLabel || GALLERY_DISPLAY_DEFAULT.sortbyDefaultLabel

  const columnsStats = sortbyOptions.map((s) => `stat-${s}`).filter((c) => isGalleryColumnSlugType(c))
  const columnsPrices = priceSources.map((ps) => `price-${ps}`).filter((c) => isGalleryColumnSlugType(c))
  const columnsOptions = [...GALLERY_COLUMN_SLUGS_CORE, ...GALLERY_COLUMN_SLUGS_META, ...columnsStats, ...columnsPrices]
  const columnsPricesPreferred = columnsPrices.slice(0, 3)

  const columnsDefault = columnsOptions.filter(
    (s) => isGalleryColumnSlugsCoreType(s) || columnsStats.includes(s) || columnsPricesPreferred.includes(s),
  )

  const columnsValid = columnsPref?.length ? columnsOptions.filter((c) => columnsPref.includes(c)) : undefined
  const columns = columnsValid?.length ? columnsValid : columnsDefault

  // How many display preferences the user has changed away from the page defaults
  const cardFiltersCount = cardFilters?.length || 0

  const editedPrefs = {
    columns: columns.join() !== columnsDefault.join(),
    groupby: !!groupbyValid && groupbyValid !== groupbyDefault,
    size: !!sizeValid && sizeValid !== sizeDefault,
    sortby: !!sortbyValid && sortbyValid !== sortbyDefault,
    sortbyDirection: !!sortbyDirectionPref && sortbyDirectionPref !== sortbyDirectionDefault,
    view: !!viewPref && viewPref !== viewDefault,
  }

  const edited = { cardFilters: cardFiltersCount > 0, ...editedPrefs }

  const editedCount = cardFiltersCount + Object.values(editedPrefs).filter(Boolean).length

  const stats = getGalleryStats({ sortby, stats: config.stats })
  const isSortbyCardRequired = GALLERY_SORTBY_SLUGS_CARD_REQUIRED.includes(sortby)
  const statsRoles =
    config.statsRoles && isSortbyCardRequired
      ? Object.fromEntries(
          Object.entries(config.statsRoles).map(([role, s]) => [role, getGalleryStats({ sortby, stats: s })]),
        )
      : config.statsRoles

  return {
    cardFiltersCount,
    columns,
    columnsOptions,
    edited,
    editedCount,
    groupby,
    groupbyDefault,
    groupbySlugs,
    size,
    sortby,
    sortbyDefault,
    sortbyDefaultIcon,
    sortbyDefaultLabel,
    sortbyDirection,
    sortbyOptions,
    stats,
    statsRoles,
    view,
  }
}
